// Tests in the code: which projects hold tests, which methods are tests (xunit, NUnit, MSTest,
// TUnit), what the framework runs with each, and what a test reaches. Project files are read as
// text, without MSBuild: properties and items, the SDK, and the Directory.Build.props above.
import type { CsAttribute, Expr, TypeRef } from '../csharp/ast';
import { attributeName, chain } from './endpoints';
import type { CodeMap, CodeMember, CodeProject, CodeTest, TestFramework } from './model';
import { reach, reachIndex, type Reach, type ReachOptions } from './reach';
import type { FileContext, MemberSymbol, SymbolTable, TypeSymbol } from './symbols';

/** A project file (`.csproj`, `Directory.Build.props`) and its text. */
export interface ProjectFile {
  path: string;
  text: string;
}

// ---------------------------------------------------------------- project files

/** What a project file says, as far as tests go. */
export interface ProjectFacts {
  sdks: string[];
  /** The last `<IsTestProject>` value, as MSBuild takes it. */
  isTestProject: boolean | null;
  /** Package references, as written. */
  packages: string[];
  /** `<Using Include="Xunit" />` items: global usings of the project. */
  usings: string[];
}

function attr(attributes: string, name: string): string | null {
  const m = new RegExp(`\\b${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`, 'i').exec(attributes);
  return m ? (m[1] ?? m[2] ?? '') : null;
}

/**
 * Reads a project file. In a `Directory.Build.props` (`conditional` false), anything under a
 * condition is left out: it often holds what only test projects get
 * (`Condition="$(MSBuildProjectName.EndsWith('Tests'))"`), which would make every project look like one.
 */
export function readProject(text: string, conditional = true): ProjectFacts {
  let xml = text.replace(/<!--[\s\S]*?-->/g, '');
  if (!conditional) {
    xml = xml.replace(/<(PropertyGroup|ItemGroup|Choose)\b[^>]*\bCondition\s*=[^>]*>[\s\S]*?<\/\1\s*>/gi, '');
    xml = xml.replace(/<(\w+)\b[^>]*\bCondition\s*=[^>]*?(?:\/>|>[\s\S]*?<\/\1\s*>)/gi, '');
  }
  const sdks: string[] = [];
  for (const m of xml.matchAll(/<(?:Project|Import|Sdk)\b([^>]*)>/gi)) {
    const sdk = attr(m[1]!, 'Sdk') ?? (/^<Sdk\b/i.test(m[0]) ? attr(m[1]!, 'Name') : null);
    if (sdk) for (const s of sdk.split(';')) if (s.trim()) sdks.push(s.trim().split('/')[0]!);
  }
  let isTestProject: boolean | null = null;
  for (const m of xml.matchAll(/<IsTestProject\b[^>]*>\s*(\w+)\s*<\/IsTestProject\s*>/gi)) isTestProject = m[1]!.toLowerCase() === 'true';
  const packages: string[] = [];
  for (const m of xml.matchAll(/<PackageReference\b([^>]*)>/gi)) {
    const name = attr(m[1]!, 'Include');
    if (name) packages.push(name.trim());
  }
  const usings: string[] = [];
  for (const m of xml.matchAll(/<Using\b([^>]*)>/gi)) {
    const name = attr(m[1]!, 'Include');
    if (name && attr(m[1]!, 'Alias') === null && attr(m[1]!, 'Static')?.toLowerCase() !== 'true') usings.push(name.trim());
  }
  return { sdks, isTestProject, packages, usings };
}

/** Packages that make a test project, and the framework each one is; the test SDK first, as the clearest sign. */
const FRAMEWORKS: TestFramework[] = ['xunit', 'nunit', 'mstest', 'tunit'];

/**
 * The test framework a package is part of, by family (`xunit.v3.mtp-v2`, `NUnit3TestAdapter`,
 * `MSTest.TestFramework`); null for Microsoft.NET.Test.Sdk; undefined for other packages.
 */
function packageFramework(name: string): TestFramework | null | undefined {
  const n = name.toLowerCase();
  if (n === 'microsoft.net.test.sdk') return null;
  return FRAMEWORKS.find((fw) => n === fw || n.startsWith(`${fw}.`) || (fw === 'nunit' && n.startsWith('nunit3')));
}

/** The package that makes a test project: the test SDK first, as the clearest sign. */
function testPackage(packages: string[]): string | null {
  return packages.find((p) => packageFramework(p) === null) ?? packages.find((p) => packageFramework(p)) ?? null;
}

/** The test frameworks a project's packages bring. */
export function frameworksOf(packages: string[]): TestFramework[] {
  return [...new Set(packages.flatMap((p) => packageFramework(p) ?? []))];
}

/** `Shop.Tests`, `Shop.UnitTests`, `Shop.IntegrationTests`, `Shop.Specs`, `Tests`. */
const TEST_NAME = /(^|[.\-_])(tests?|unittests?|integrationtests?|functionaltests?|acceptancetests?|componenttests?|e2etests?|endtoendtests?|specs?|specifications)$/i;

function folderOf(path: string): string {
  return path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
}

function baseName(path: string): string {
  return path.slice(path.lastIndexOf('/') + 1);
}

/** Whether a folder is `inner` or holds it. */
function holds(folder: string, inner: string): boolean {
  return folder === '' || inner === folder || inner.startsWith(`${folder}/`);
}

/** A project, its facts, and the facts of the `Directory.Build.props` above it, nearest first. */
export interface ProjectInfo {
  path: string;
  folder: string;
  name: string;
  facts: ProjectFacts;
  props: { path: string; facts: ProjectFacts }[];
}

export function projectInfos(projects: ProjectFile[], props: ProjectFile[]): ProjectInfo[] {
  const propFacts = props.map((p) => ({ path: p.path, folder: folderOf(p.path), facts: readProject(p.text, false) })).sort((a, b) => b.folder.length - a.folder.length);
  return projects.map((p) => {
    const folder = folderOf(p.path);
    return {
      path: p.path,
      folder,
      name: baseName(p.path).replace(/\.csproj$/i, ''),
      facts: readProject(p.text),
      props: propFacts.filter((x) => holds(x.folder, folder)).map(({ path, facts }) => ({ path, facts })),
    };
  });
}

/** Whether a project holds tests, and why (see `CodeProject.test`). */
export function classifyProject(info: ProjectInfo, declaresTests: boolean): CodeProject {
  const out = (test: boolean, why: string | null): CodeProject => ({ path: info.path, folder: info.folder, name: info.name, test, why });
  if (info.facts.isTestProject !== null) return out(info.facts.isTestProject, `IsTestProject is ${info.facts.isTestProject}`);
  for (const p of info.props) if (p.facts.isTestProject !== null) return out(p.facts.isTestProject, `IsTestProject is ${p.facts.isTestProject} in ${p.path}`);
  if (info.facts.sdks.some((s) => s.toLowerCase() === 'mstest.sdk')) return out(true, 'uses MSTest.Sdk');
  const own = testPackage(info.facts.packages);
  if (own) return out(true, `references ${own}`);
  for (const p of info.props) {
    const found = testPackage(p.facts.packages);
    if (found) return out(true, `references ${found} in ${p.path}`);
  }
  if (TEST_NAME.test(info.name)) return out(true, 'named like a test project');
  if (declaresTests) return out(true, 'declares tests');
  return out(false, null);
}

/** For a file in no project: a folder named like tests (`tests/`, `Shop.Tests/`) on its path. */
export function looksLikeTestPath(path: string): boolean {
  return path.split('/').slice(0, -1).some((s) => TEST_NAME.test(s));
}

// ---------------------------------------------------------------- tests

/** Attributes that make a method a test, and the frameworks that have them. */
const TEST_ATTRIBUTES: Record<string, TestFramework[]> = {
  Fact: ['xunit'],
  Theory: ['xunit', 'nunit'],
  Test: ['nunit', 'tunit'],
  TestCase: ['nunit'],
  TestCaseSource: ['nunit'],
  TestMethod: ['mstest'],
  DataTestMethod: ['mstest'],
};

/** Attributes giving one row of arguments: each is a run of the test. */
const CASE_ATTRIBUTES = new Set(['InlineData', 'TestCase', 'DataRow', 'Arguments']);

/** Set-up and tear-down methods: NUnit, MSTest, TUnit. */
const SETUP_ATTRIBUTES = new Set([
  'SetUp', 'TearDown', 'OneTimeSetUp', 'OneTimeTearDown', 'TestInitialize', 'TestCleanup', 'ClassInitialize', 'ClassCleanup',
  'Before', 'After', 'BeforeEvery', 'AfterEvery',
]);

/** Interfaces the framework calls through: xunit's async set-up, and disposal after each test. */
const LIFETIME_METHODS: Record<string, string[]> = {
  IAsyncLifetime: ['InitializeAsync', 'DisposeAsync'],
  IDisposable: ['Dispose'],
  IAsyncDisposable: ['DisposeAsync'],
};

const NAMESPACE_FRAMEWORKS: Record<string, TestFramework> = {
  Xunit: 'xunit',
  'NUnit.Framework': 'nunit',
  'Microsoft.VisualStudio.TestTools.UnitTesting': 'mstest',
  'TUnit.Core': 'tunit',
};

/** The frameworks that could own a test attribute: by name, or by the ending of a custom one (`SkippableFact`, `UITestMethod`). */
function testAttribute(a: CsAttribute): TestFramework[] | null {
  const name = attributeName(a);
  if (TEST_ATTRIBUTES[name]) return TEST_ATTRIBUTES[name];
  if (/(Fact|Theory)$/.test(name)) return ['xunit'];
  if (/TestMethod$/.test(name)) return ['mstest'];
  return null;
}

/** Which framework a test is: the attribute's namespace, the namespaces in scope, the project's packages, then the attribute alone. */
function frameworkOf(a: CsAttribute, candidates: TestFramework[], usings: string[], packages: TestFramework[]): TestFramework {
  for (const [ns, fw] of Object.entries(NAMESPACE_FRAMEWORKS)) if (a.name.startsWith(`${ns}.`) && candidates.includes(fw)) return fw;
  const inScope = candidates.filter((fw) => usings.some((u) => NAMESPACE_FRAMEWORKS[u] === fw));
  if (inScope.length === 1) return inScope[0]!;
  const referenced = candidates.filter((fw) => packages.includes(fw));
  if (referenced.length === 1) return referenced[0]!;
  return (inScope[0] ?? referenced[0] ?? candidates[0])!;
}

function attributesOfMember(m: MemberSymbol): CsAttribute[] {
  return m.decl.kind === 'method' ? m.decl.method.attributes : m.decl.kind === 'property' ? m.decl.property.attributes : [];
}

function skippedBy(attributes: CsAttribute[], test: CsAttribute): boolean {
  if (test.args.some((x) => x.name === 'Skip' && !(x.value.kind === 'literal' && x.value.literal === 'null'))) return true;
  return attributes.some((a) => {
    const name = attributeName(a);
    return name === 'Ignore' || name === 'Skip';
  });
}

/** The last part of a written base type: `IClassFixture` for `Xunit.IClassFixture<ShopFactory>`. */
function lastName(ref: TypeRef): string {
  return ref.name.slice(ref.name.lastIndexOf('.') + 1);
}

/** ASP.NET Core's test host: what a `WebApplicationFactory<T>` subclass overrides runs when it starts the app. */
const TEST_HOST_OVERRIDES = new Set(['ConfigureWebHost', 'CreateHost', 'CreateHostBuilder', 'CreateWebHostBuilder', 'ConfigureClient']);

/** The string a constant gives in an attribute: `"Api"`, `ApiCollection.Name`, `Name`, `nameof(Api)`. */
function constString(table: SymbolTable, e: Expr | undefined, sym: TypeSymbol, ctx: FileContext, depth = 0): string | null {
  if (!e || depth > 4) return null;
  if (e.kind === 'literal' && e.literal === 'string') return String(e.value);
  if (e.kind === 'call' && e.callee.kind === 'name' && e.callee.name === 'nameof') {
    const of = e.args[0]?.value;
    return of?.kind === 'name' || of?.kind === 'member' ? of.name : null;
  }
  let owner: TypeSymbol | null = null;
  let name: string | null = null;
  if (e.kind === 'name') {
    name = e.name;
    for (let t: TypeSymbol | null = sym; t && !owner; t = t.outer) if (t.byName.has(e.name)) owner = t;
  } else if (e.kind === 'member' && e.target.kind === 'name') {
    const ty = table.resolve({ name: e.target.name, args: [], nullable: false, arrays: [], span: e.span }, { ctx, type: sym, typeParams: [] });
    if (ty.k === 'code') {
      owner = ty.sym;
      name = e.name;
    }
  }
  const field = name ? owner?.byName.get(name)?.find((m) => m.decl.kind === 'field') : undefined;
  return field?.decl.kind === 'field' ? constString(table, field.decl.init ?? undefined, owner!, field.ctx, depth + 1) : null;
}

/** xunit's collection definitions by name: `[CollectionDefinition("Api")]` classes. */
function collectionDefinitions(table: SymbolTable): Map<string, TypeSymbol> {
  const out = new Map<string, TypeSymbol>();
  for (const sym of table.typeOrder) {
    for (const part of sym.parts) {
      const a = part.cls.attributes.find((x) => attributeName(x) === 'CollectionDefinition');
      const name = a ? constString(table, a.args.find((x) => x.name === null)?.value, sym, part.ctx) : null;
      if (name !== null) out.set(name, sym);
    }
  }
  return out;
}

/**
 * What the framework runs with each test of a class: constructors, field and property initializers
 * that make calls, set-up and tear-down methods, and xunit fixtures (class and collection fixtures:
 * constructors, async set-up, and a test host's overrides). Base classes too.
 */
function setupOf(table: SymbolTable, sym: TypeSymbol, withCalls: Set<string>, collections: Map<string, TypeSymbol>): string[] {
  const out: string[] = [];
  const add = (m: MemberSymbol) => {
    if (!out.includes(m.id)) out.push(m.id);
  };
  const lifetime = (t: TypeSymbol) => {
    for (const part of t.parts) {
      for (const ref of part.cls.baseTypes) {
        for (const name of LIFETIME_METHODS[lastName(ref)] ?? []) for (const m of t.byName.get(name) ?? []) if (m.kind === 'method' && !m.params.length) add(m);
      }
    }
  };
  // Base classes first: their constructors and set-up run before the class's own.
  for (const t of chain(table, sym).reverse()) {
    for (const m of t.members) {
      if (m.kind === 'constructor' && !m.isStatic) add(m);
      else if ((m.kind === 'field' || m.kind === 'property') && withCalls.has(m.id)) add(m);
      else if (m.kind === 'method' && attributesOfMember(m).some((a) => SETUP_ATTRIBUTES.has(attributeName(a)))) add(m);
    }
    lifetime(t);
    // xunit's fixtures: made once for the class or its collection (`[Collection("Api")]`), and handed to its constructor.
    const holders = [t];
    for (const part of t.parts) {
      const a = part.cls.attributes.find((x) => attributeName(x) === 'Collection');
      const name = a ? constString(table, a.args.find((x) => x.name === null)?.value, t, part.ctx) : null;
      const definition = name !== null ? collections.get(name) : undefined;
      if (definition && !holders.includes(definition)) holders.push(definition);
    }
    for (const holder of holders) {
      for (const part of holder.parts) {
        for (const ref of part.cls.baseTypes) {
          const name = lastName(ref);
          if ((name !== 'IClassFixture' && name !== 'ICollectionFixture') || ref.args.length !== 1) continue;
          const fixture = table.resolve(ref.args[0]!, { ctx: part.ctx, type: holder, typeParams: [] });
          if (fixture.k !== 'code') continue;
          const testHost = fixture.sym.parts.some((p) => p.cls.baseTypes.some((b) => lastName(b) === 'WebApplicationFactory'));
          for (const m of fixture.sym.members) {
            if (m.kind === 'constructor' && !m.isStatic) add(m);
            else if ((m.kind === 'field' || m.kind === 'property') && withCalls.has(m.id)) add(m);
            else if (testHost && m.kind === 'method' && m.modifiers.includes('override') && TEST_HOST_OVERRIDES.has(m.name)) add(m);
          }
          lifetime(fixture.sym);
        }
      }
    }
  }
  return out;
}

/**
 * The tests of the code. `packagesOf` gives the test frameworks a file's project references, and
 * `withCalls` the members that make calls (for initializers worth listing as set-up).
 */
export function findTests(table: SymbolTable, packagesOf: (path: string) => TestFramework[], withCalls: Set<string>): CodeTest[] {
  const out: CodeTest[] = [];
  const setups = new Map<TypeSymbol, string[]>();
  const collections = collectionDefinitions(table);
  for (const sym of table.typeOrder) {
    if (sym.kind !== 'class' && sym.kind !== 'record') continue;
    for (const m of sym.members) {
      if (m.kind !== 'method') continue;
      const attributes = attributesOfMember(m);
      let found: { a: CsAttribute; candidates: TestFramework[] } | null = null;
      for (const a of attributes) {
        const candidates = testAttribute(a);
        if (candidates) {
          found = { a, candidates };
          break;
        }
      }
      if (!found) continue;
      let setup = setups.get(sym);
      if (!setup) setups.set(sym, (setup = setupOf(table, sym, withCalls, collections)));
      out.push({
        id: m.id,
        type: sym.id,
        name: m.name,
        framework: frameworkOf(found.a, found.candidates, m.ctx.usings, packagesOf(m.ctx.path)),
        attribute: attributeName(found.a),
        cases: attributes.filter((a) => CASE_ATTRIBUTES.has(attributeName(a))).length,
        skipped: skippedBy(attributes, found.a),
        setup,
        file: m.ctx.path,
        line: m.line,
      });
    }
  }
  return out.sort((a, b) => (a.file !== b.file ? (a.file < b.file ? -1 : 1) : a.line - b.line));
}

// ---------------------------------------------------------------- reach

export interface TestReach extends Reach {
  test: CodeTest;
  /**
   * Types the test puts a fake in place of: mocked (`Mock<IOrderRepository>`, `Substitute.For<IPricing>()`),
   * or implemented by a test class it creates (`by`). Calls to their members stop there, or go on
   * only to the fake.
   */
  fakes: { type: string; by: string | null }[];
}

/** What every test's reach needs, worked out once per code map. */
interface TestContext {
  index: ReturnType<typeof reachIndex>;
  /** Base types and interfaces of a type in the code, all the way up. */
  supertypes: (id: string) => string[];
  /** The types of the code a fake in a test file stands in for: the nearest ones outside test code. */
  replaced: (id: string) => string[];
  fileOfType: (id: string) => string;
  membersOfType: Map<string, string[]>;
  /** Types each member or type fakes (`CodeUse` of kind `mock`). */
  mocksFrom: Map<string, string[]>;
  /** The test class and its base classes. */
  testClasses: (type: string) => string[];
}

const contexts = new WeakMap<CodeMap, TestContext>();

function testContext(map: CodeMap): TestContext {
  const cached = contexts.get(map);
  if (cached) return cached;
  const index = reachIndex(map);
  const types = new Map(map.types.map((t) => [t.id, t]));
  const fileOfType = (id: string) => types.get(id)?.parts[0]?.file ?? '';
  const memo = (walk: (id: string) => string[]) => {
    const known = new Map<string, string[]>();
    return (id: string) => {
      let out = known.get(id);
      if (!out) known.set(id, (out = walk(id)));
      return out;
    };
  };
  // Up through base types and interfaces; `stop` ends a path at a type (and keeps it).
  const up = (id: string, stop: (t: string) => boolean): string[] => {
    const out: string[] = [];
    const queue = [id];
    const seen = new Set<string>();
    while (queue.length) {
      const t = types.get(queue.shift()!);
      if (!t) continue;
      for (const s of [...(t.base ? [t.base] : []), ...t.interfaces]) {
        if (seen.has(s)) continue;
        seen.add(s);
        out.push(s);
        if (!stop(s)) queue.push(s);
      }
    }
    return out;
  };
  const supertypes = memo((id) => up(id, () => false));
  const replaced = memo((id) => up(id, (s) => !index.testFiles.has(fileOfType(s))).filter((s) => !index.testFiles.has(fileOfType(s))));
  const membersOfType = new Map<string, string[]>();
  for (const m of map.members) membersOfType.set(m.type, [...(membersOfType.get(m.type) ?? []), m.id]);
  const mocksFrom = new Map<string, string[]>();
  for (const u of map.uses) if (u.kind === 'mock') mocksFrom.set(u.from, [...(mocksFrom.get(u.from) ?? []), u.to]);
  const testClasses = memo((type) => [type, ...supertypes(type).filter((t) => types.get(t)?.kind !== 'interface')]);
  const context = { index, supertypes, replaced, fileOfType, membersOfType, mocksFrom, testClasses };
  contexts.set(map, context);
  return context;
}

/**
 * Everything a test reaches: the test method and what runs with it, through the code under test.
 * A type the test fakes is not followed into its real implementations, and fakes in test files
 * are followed only when this test creates them.
 */
export function testReach(map: CodeMap, testId: string, options: ReachOptions = {}): TestReach | null {
  const test = map.tests.find((t) => t.id === testId);
  return test ? reachOfTest(map, testContext(map), test, options) : null;
}

function reachOfTest(map: CodeMap, ctx: TestContext, test: CodeTest, options: ReachOptions): TestReach {
  const { index, supertypes, replaced, fileOfType } = ctx;
  // The test class and its bases: what their members fake counts for every test of the class.
  const testClasses = ctx.testClasses(test.type);
  const classMembers = testClasses.flatMap((t) => ctx.membersOfType.get(t) ?? []);

  // Fakes found in the reach can change it: go again until they stop changing (a few rounds at most).
  let mocked = new Set<string>();
  let created = new Set<string>(testClasses);
  for (let round = 0; ; round++) {
    // A faked type stands in for its supertypes too: mocking IOrderRepository replaces IRepository<Order>.
    const standIns = new Map<string, string | null>();
    for (const t of mocked) standIns.set(t, null);
    for (const c of created) {
      if (testClasses.includes(c)) continue;
      for (const s of replaced(c)) if (!standIns.has(s)) standIns.set(s, c);
    }
    const faked = new Set([...standIns.keys()].flatMap((t) => [t, ...supertypes(t)]));
    // Fakes in test files run when the test creates them (or a type deriving from them).
    const live = new Set([...created].flatMap((c) => [c, ...supertypes(c)]));
    const found = reach(
      map,
      {
        members: [test.id, ...test.setup],
        file: test.file,
        requests: true,
        dispatchTo: (member: CodeMember, impl: CodeMember) => (index.testFiles.has(impl.file) ? live.has(impl.type) : !faked.has(member.type)),
      },
      options,
    );
    const nextMocked = new Set<string>();
    for (const id of [...classMembers, ...found.depth.keys()]) for (const t of ctx.mocksFrom.get(id) ?? []) nextMocked.add(t);
    const nextCreated = new Set(testClasses);
    for (const c of found.calls) {
      if (c.kind !== 'new') continue;
      const type = index.members.get(c.to)?.type ?? c.to;
      if (index.testFiles.has(fileOfType(type))) nextCreated.add(type);
    }
    if (round === 3 || (same(nextMocked, mocked) && same(nextCreated, created))) {
      const fakes = [...standIns].map(([type, by]) => ({ type, by })).sort((a, b) => (a.type < b.type ? -1 : a.type > b.type ? 1 : 0));
      return { test, ...found, fakes };
    }
    mocked = nextMocked;
    created = nextCreated;
  }
}

function same(a: Set<string>, b: Set<string>): boolean {
  return a.size === b.size && [...a].every((x) => b.has(x));
}

/** A test reaching a member: `depth` 0 when it runs with the test (set-up), 1 when the test calls it. */
export interface TestedBy {
  test: string;
  depth: number;
  /** For an endpoint: whether the test sends it an HTTP request, or calls its handler. */
  via?: 'request' | 'handler';
}

const testedByCache = new WeakMap<CodeMap, Map<string, { members: Map<string, TestedBy[]>; endpoints: Map<string, TestedBy[]> }>>();

/** The tests reaching each member and each endpoint, worked out for every test at once and kept per code map. */
function testsReaching(map: CodeMap, options: ReachOptions): { members: Map<string, TestedBy[]>; endpoints: Map<string, TestedBy[]> } {
  const key = `${options.guesses ?? true}|${options.fields ?? false}|${options.limit ?? ''}`;
  let byOptions = testedByCache.get(map);
  if (!byOptions) testedByCache.set(map, (byOptions = new Map()));
  const cached = byOptions.get(key);
  if (cached) return cached;
  const ctx = testContext(map);
  const members = new Map<string, TestedBy[]>();
  const endpoints = new Map<string, TestedBy[]>();
  const add = (into: Map<string, TestedBy[]>, id: string, entry: TestedBy) => {
    const list = into.get(id);
    if (!list) into.set(id, [entry]);
    else if (!list.some((x) => x.test === entry.test)) list.push(entry);
  };
  for (const test of map.tests) {
    const found = reachOfTest(map, ctx, test, options);
    for (const [id, depth] of found.depth) if (id !== test.id) add(members, id, { test: test.id, depth });
    // An endpoint is reached by the tests sending it a request, and by those calling its handler.
    for (const r of found.requests) add(endpoints, r.endpoint, { test: test.id, depth: (found.depth.get(r.from) ?? 0) + 1, via: 'request' });
    for (const e of map.endpoints) {
      const depth = e.handler ? found.depth.get(e.handler) : undefined;
      if (depth !== undefined) add(endpoints, e.id, { test: test.id, depth, via: 'handler' });
    }
  }
  for (const list of [...members.values(), ...endpoints.values()]) list.sort((a, b) => a.depth - b.depth);
  const out = { members, endpoints };
  byOptions.set(key, out);
  return out;
}

/**
 * For each member, the tests that reach it (as `testReach` follows them), nearest first. A test
 * does not count for its own method. Worked out for every test at once, and kept per code map.
 */
export function testedBy(map: CodeMap, options: ReachOptions = {}): Map<string, TestedBy[]> {
  return testsReaching(map, options).members;
}

/** For each endpoint, the tests that send it a request or call its handler, nearest first. */
export function endpointTestedBy(map: CodeMap, options: ReachOptions = {}): Map<string, TestedBy[]> {
  return testsReaching(map, options).endpoints;
}
